import { supabase } from '../../lib/supabase';
import type { ClientType, ManualRegistrationData } from './types';

export interface ContractDraft {
  id: string;
  client_type: ClientType;
  current_step: number;
  contract_temp_id: string;
  client_name: string;
  client_cpf: string;
  data: unknown;
  prefilled: boolean;
  updated_at: string;
}

interface StoredDocument {
  category: string;
  label: string;
  urls: string[];
  fileMeta: { name: string; type: string }[];
}

function serialize(data: ManualRegistrationData) {
  return {
    ...data,
    loan: { ...data.loan, installmentDates: data.loan.installmentDates.map(d => d.toISOString()) },
    contacts: data.contacts.map(c => ({ ...c, photoFile: null, conversationFile: null })),
    documents: data.documents.map<StoredDocument>(d => ({
      category: d.category,
      label: d.label,
      urls: d.urls,
      fileMeta: d.urls.map((_, i) => ({ name: d.files[i]?.name || `arquivo-${i + 1}`, type: d.files[i]?.type || '' })),
    })),
  };
}

export function deserializeDraft(draft: ContractDraft): ManualRegistrationData {
  const raw = draft.data as ReturnType<typeof serialize>;
  return {
    ...raw,
    clientType: draft.client_type,
    declarationConfirmed: false,
    loan: { ...raw.loan, installmentDates: (raw.loan.installmentDates || []).map(s => new Date(s)) },
    documents: (raw.documents || []).map(d => ({
      category: d.category,
      label: d.label,
      urls: d.urls,
      files: d.fileMeta.map(m => new File([], m.name, { type: m.type })),
      uploading: false,
    })),
  } as ManualRegistrationData;
}

export async function saveDraft(params: {
  id: string | null;
  data: ManualRegistrationData;
  step: number;
  contractTempId: string;
  prefilled: boolean;
}): Promise<string> {
  const row = {
    client_type: params.data.clientType,
    current_step: params.step,
    contract_temp_id: params.contractTempId,
    client_name: params.data.personal.name,
    client_cpf: params.data.personal.cpf,
    data: serialize(params.data),
    prefilled: params.prefilled,
    updated_at: new Date().toISOString(),
  };
  const query = params.id
    ? supabase.from('contract_drafts').update(row).eq('id', params.id).select('id').maybeSingle()
    : supabase.from('contract_drafts').insert(row).select('id').maybeSingle();
  const { data, error } = await query;
  if (error || !data) throw error || new Error('Draft not saved');
  return data.id as string;
}

export async function listDrafts(): Promise<ContractDraft[]> {
  const { data, error } = await supabase
    .from('contract_drafts')
    .select('id, client_type, current_step, contract_temp_id, client_name, client_cpf, data, prefilled, updated_at')
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data || []) as ContractDraft[];
}

export async function deleteDraft(id: string) {
  const { error } = await supabase.from('contract_drafts').delete().eq('id', id);
  if (error) throw error;
}
